import { useEffect, useState } from 'react'
import { track } from '../../utils/analytics.js'

// Sign-in modal with three methods:
//   1. Create a new account (default for users without a Nostr extension —
//      the mainstream signup flow; UX28 renamed from "Create new")
//   2. Import an existing key (nsec / hex / ncryptsec) — Nostr natives
//   3. Browser extension (NIP-07) — Nostr natives with an extension installed
//
// UX28 hides the tab UI and promotes Create to the primary view. The other
// two paths are reachable via switcher links below the primary form.
//
// Renders an "Unlock" view instead when mode === 'unlock' — used when the
// user already has an encrypted key on disk and needs to enter a passphrase.

export function SignInModal({
  open,
  mode = 'choose',
  defaultTab, // UX30: when set, overrides the auto-select on open
  nostrAvailable,
  error,
  clearError,
  onClose,
  onLoginNip07,
  onLoginWithGoogle,
  onImport,
  onCreate,
  onUnlock,
  onLogout,
}) {
  const [tab, setTab] = useState('extension')
  const [importValue, setImportValue] = useState('')
  // Import defaults to encrypted (importers historically chose it by
  // default; preserve their flow). Create no longer exposes a toggle —
  // UX28 flipped encrypt-default on and hides the choice (mainstream
  // users don't want to decide crypto options at signup).
  const [encryptImport, setEncryptImport] = useState(true)
  const [passphrase, setPassphrase] = useState('')
  const [passphrase2, setPassphrase2] = useState('')
  const [generated, setGenerated] = useState(null) // { pubkey, nsec }
  const [busy, setBusy] = useState(false)
  const [unlockPass, setUnlockPass] = useState('')

  // Reset transient state whenever the modal closes.
  useEffect(() => {
    if (!open) {
      setImportValue('')
      setPassphrase('')
      setPassphrase2('')
      setGenerated(null)
      setBusy(false)
      setUnlockPass('')
      clearError?.()
    }
  }, [open, clearError])

  // When opening, honor an explicit defaultTab if the opener passed one
  // (the UX30 Create-account button does this). Otherwise fall back to
  // the auto-select: Extension if the user has a NIP-07 extension, else
  // Create for the mainstream path.
  useEffect(() => {
    if (open && mode === 'choose') {
      if (defaultTab) {
        setTab(defaultTab)
      } else {
        setTab(nostrAvailable ? 'extension' : 'create')
      }
    }
  }, [open, mode, nostrAvailable, defaultTab])

  // Escape key dismisses the modal — soft close, no logout.
  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  // Funnel: signin_started fires once per modal open, regardless of
  // mode. signin_completed lives in useNostrLogin's success paths.
  useEffect(() => {
    if (open) track('signin_started', { mode })
  }, [open, mode])

  if (!open) return null

  const isUnlock = mode === 'unlock'
  const isGoogleUnlock = mode === 'google-unlock'

  const handleNip07 = async () => {
    setBusy(true)
    try {
      const pk = await onLoginNip07()
      if (pk) onClose()
    } finally {
      setBusy(false)
    }
  }

  const handleImport = async (e) => {
    e.preventDefault()
    if (!importValue.trim()) return
    if (encryptImport && !passphrase) return
    setBusy(true)
    try {
      const pk = await onImport(importValue, encryptImport ? { passphrase } : {})
      if (pk) onClose()
    } finally {
      setBusy(false)
    }
  }

  const handleCreate = async (e) => {
    e.preventDefault()
    if (!passphrase) return
    if (passphrase !== passphrase2) return
    setBusy(true)
    try {
      const result = await onCreate({ passphrase })
      if (result) setGenerated(result)
    } finally {
      setBusy(false)
    }
  }

  const handleUnlock = async (e) => {
    e.preventDefault()
    if (!unlockPass) return
    setBusy(true)
    try {
      const ok = await onUnlock(unlockPass)
      if (ok) onClose()
    } finally {
      setBusy(false)
    }
  }

  // Only Create has the confirm field (passphrase2 is only populated there),
  // so a mismatch is Create-only in practice.
  const passphraseMismatch =
    passphrase && passphrase2 && passphrase !== passphrase2

  const switchTab = (next) => {
    setTab(next)
    setPassphrase('')
    setPassphrase2('')
    clearError?.()
  }

  const title = isUnlock ? 'Unlock your account'
    : isGoogleUnlock ? 'Welcome back'
    : generated ? 'Save your recovery key'
    : tab === 'create' ? 'Create your account'
    : 'Sign in to Verdant'

  return (
    <div
      className="modal-backdrop"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="signin-title"
    >
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <header className="modal-head">
          <h2 id="signin-title">{title}</h2>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label="Close"
          >×</button>
        </header>

        {isUnlock ? (
          <UnlockView
            error={error}
            unlockPass={unlockPass}
            setUnlockPass={setUnlockPass}
            busy={busy}
            onSubmit={handleUnlock}
            onSwitchAccount={() => { onLogout?.(); onClose() }}
          />
        ) : isGoogleUnlock ? (
          <GoogleUnlockView
            error={error}
            onContinue={() => { onLoginWithGoogle?.(); /* page is about to
              redirect to Google; modal unmounts implicitly */ }}
            onSwitchAccount={() => { onLogout?.(); onClose() }}
          />
        ) : generated ? (
          <BackupView
            generated={generated}
            encrypted
            onDone={onClose}
          />
        ) : (
          <div className="modal-body">
            {tab === 'create' && (
              <>
                <GoogleSignInButton onClick={onLoginWithGoogle} />
                <OrDivider />
                <CreateView
                  passphrase={passphrase}
                  setPassphrase={setPassphrase}
                  passphrase2={passphrase2}
                  setPassphrase2={setPassphrase2}
                  passphraseMismatch={passphraseMismatch}
                  busy={busy}
                  onSubmit={handleCreate}
                />
              </>
            )}

            {tab === 'import' && (
              <ImportView
                importValue={importValue}
                setImportValue={setImportValue}
                encrypt={encryptImport}
                setEncrypt={setEncryptImport}
                passphrase={passphrase}
                setPassphrase={setPassphrase}
                busy={busy}
                onSubmit={handleImport}
              />
            )}

            {tab === 'extension' && (
              <>
                <GoogleSignInButton onClick={onLoginWithGoogle} />
                <OrDivider />
                <ExtensionView
                  nostrAvailable={nostrAvailable}
                  busy={busy}
                  onLogin={handleNip07}
                />
              </>
            )}

            {error && <div className="error-state" style={{ marginTop: 12 }}>{error}</div>}

            <SignInSwitcher currentTab={tab} onSwitch={switchTab} />
          </div>
        )}
      </div>
    </div>
  )
}

// UX29: Google sign-in button per Google's branding guidelines
// (developers.google.com/identity/branding-guidelines). The official G
// mark is a specific four-color SVG; text is "Sign in with Google" at
// 14px Roboto medium; button has a specific border color and height.
// Styles live in index.css under .btn-google. Rendered inert if the
// handler isn't wired (so dev / preview without env vars doesn't error
// on click).
function GoogleSignInButton({ onClick }) {
  return (
    <button
      type="button"
      className="btn btn-google btn-block"
      onClick={onClick}
      disabled={!onClick}
    >
      <GoogleGMark />
      <span>Sign in with Google</span>
    </button>
  )
}

function GoogleGMark() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 48 48"
      width="18"
      height="18"
      aria-hidden="true"
    >
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A833" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  )
}

function OrDivider() {
  return <div className="signin-or" aria-hidden="true"><span>or</span></div>
}

// Links to the other two sign-in methods. Replaces the UX27-era tab bar;
// mainstream signup should not look like a crypto tool with three equal
// paths to choose between. UX28.
function SignInSwitcher({ currentTab, onSwitch }) {
  return (
    <div className="signin-switcher">
      {currentTab !== 'create' && (
        <button type="button" className="link-button" onClick={() => onSwitch('create')}>
          Create a new account
        </button>
      )}
      {currentTab !== 'import' && (
        <button type="button" className="link-button" onClick={() => onSwitch('import')}>
          I already have an account
        </button>
      )}
      {currentTab !== 'extension' && (
        <button type="button" className="link-button" onClick={() => onSwitch('extension')}>
          Use a browser extension
        </button>
      )}
    </div>
  )
}

function ExtensionView({ nostrAvailable, busy, onLogin }) {
  return (
    <div>
      <p className="card-summary">
        Sign in with a Nostr browser extension. Your private key never leaves
        the extension — Verdant only sees signed events.
      </p>
      {nostrAvailable ? (
        <button
          type="button"
          className="btn btn-primary btn-block"
          onClick={onLogin}
          disabled={busy}
          style={{ marginTop: 12 }}
        >
          {busy ? 'Connecting…' : 'Connect with extension'}
        </button>
      ) : (
        <div className="note" style={{ marginTop: 12 }}>
          No extension detected. Install one of:
          <ul style={{ margin: '8px 0 0 18px', padding: 0 }}>
            <li><a href="https://getalby.com/" target="_blank" rel="noreferrer">Alby</a></li>
            <li><a href="https://github.com/fiatjaf/nos2x" target="_blank" rel="noreferrer">Nos2x</a></li>
          </ul>
        </div>
      )}
    </div>
  )
}

function ImportView({
  importValue, setImportValue,
  encrypt, setEncrypt,
  passphrase, setPassphrase,
  busy, onSubmit,
}) {
  return (
    <form onSubmit={onSubmit}>
      <div className="field">
        <label>Your key</label>
        <textarea
          rows={3}
          value={importValue}
          onChange={(e) => setImportValue(e.target.value)}
          placeholder="nsec1… or 64-char hex private key"
          autoComplete="off"
          spellCheck={false}
          required
        />
      </div>

      <PassphraseSection
        encrypt={encrypt}
        setEncrypt={setEncrypt}
        passphrase={passphrase}
        setPassphrase={setPassphrase}
        showConfirm={false}
        helperText={
          encrypt
            ? 'Protects your key on this device. You will re-enter this passphrase when you return.'
            : 'Your key will be stored unencrypted in this browser. Convenient but exposed to malicious scripts.'
        }
      />

      <button
        type="submit"
        className="btn btn-primary btn-block"
        disabled={busy || !importValue.trim() || (encrypt && !passphrase)}
      >
        {busy ? 'Importing…' : 'Import key'}
      </button>
    </form>
  )
}

// UX28: Create flow is now the default view for mainstream signups.
// Encryption is required and the toggle is hidden — the user picks a
// passphrase, nothing else. Vocabulary stays free of "Nostr", "key",
// and "nsec" everywhere the mainstream user can see them.
function CreateView({
  passphrase, setPassphrase,
  passphrase2, setPassphrase2,
  passphraseMismatch,
  busy, onSubmit,
}) {
  return (
    <form onSubmit={onSubmit}>
      <p className="card-summary">
        Pick a passphrase to protect your account on this device. You'll see
        your recovery key next, so you can sign in from another device later.
      </p>

      <PassphraseSection
        encrypt
        hideToggle
        passphrase={passphrase}
        setPassphrase={setPassphrase}
        showConfirm
        passphrase2={passphrase2}
        setPassphrase2={setPassphrase2}
        passphraseMismatch={passphraseMismatch}
        helperText={
          'You will re-enter this passphrase when you return after closing the browser. Lose the passphrase and you lose the account. There is no password reset.'
        }
      />

      <button
        type="submit"
        className="btn btn-primary btn-block"
        disabled={busy || !passphrase || passphraseMismatch}
      >
        {busy ? 'Creating account…' : 'Create account'}
      </button>
    </form>
  )
}

function PassphraseSection({
  encrypt, setEncrypt,
  passphrase, setPassphrase,
  passphrase2, setPassphrase2,
  showConfirm, passphraseMismatch,
  hideToggle,
  helperText,
}) {
  return (
    <>
      {!hideToggle && (
        <label
          className="checkbox-row"
          style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}
        >
          <input
            type="checkbox"
            checked={encrypt}
            onChange={(e) => setEncrypt(e.target.checked)}
          />
          Protect with a passphrase
        </label>
      )}

      {encrypt && (
        <>
          <div className="field">
            <label>Passphrase</label>
            <input
              type="password"
              value={passphrase}
              onChange={(e) => setPassphrase(e.target.value)}
              placeholder="At least 8 characters"
              autoComplete="new-password"
              minLength={8}
              required
            />
          </div>
          {showConfirm && (
            <div className="field">
              <label>Confirm passphrase</label>
              <input
                type="password"
                value={passphrase2}
                onChange={(e) => setPassphrase2(e.target.value)}
                autoComplete="new-password"
                required
              />
              {passphraseMismatch && (
                <div className="form-hint hint-warn">Passphrases do not match.</div>
              )}
            </div>
          )}
        </>
      )}

      {helperText && <div className="note">{helperText}</div>}
    </>
  )
}

// UX29 Phase 1.5: shown when a returning Google user opens the app in
// a fresh tab. localStorage knows they signed in via Google (pubkey +
// loginType='google') but sessionStorage lost the decrypted key on tab
// close. Rather than silently redirect to Google OAuth on page load,
// we show this confirm-and-continue step so the user can switch
// accounts if they want.
function GoogleUnlockView({ error, onContinue, onSwitchAccount }) {
  return (
    <div className="modal-body">
      <p className="card-summary">
        Sign in with Google to continue. You'll be redirected to Google
        for a moment and brought right back.
      </p>
      <button
        type="button"
        className="btn btn-primary btn-block"
        onClick={onContinue}
      >
        Sign in with Google
      </button>
      <button
        type="button"
        className="btn btn-secondary btn-block"
        onClick={onSwitchAccount}
        style={{ marginTop: 8 }}
      >
        Use a different account
      </button>
      {error && <div className="error-state" style={{ marginTop: 12 }}>{error}</div>}
    </div>
  )
}

function UnlockView({ error, unlockPass, setUnlockPass, busy, onSubmit, onSwitchAccount }) {
  return (
    <form onSubmit={onSubmit} className="modal-body">
      <p className="card-summary">
        Your account is protected on this device. Enter your passphrase to
        unlock it for this session.
      </p>
      <div className="field">
        <label>Passphrase</label>
        <input
          type="password"
          value={unlockPass}
          onChange={(e) => setUnlockPass(e.target.value)}
          autoFocus
          autoComplete="current-password"
          required
        />
      </div>
      <button
        type="submit"
        className="btn btn-primary btn-block"
        disabled={busy || !unlockPass}
      >
        {busy ? 'Unlocking…' : 'Unlock'}
      </button>
      <button
        type="button"
        className="btn btn-secondary btn-block"
        onClick={onSwitchAccount}
        style={{ marginTop: 8 }}
      >
        Use a different account
      </button>
      {error && <div className="error-state" style={{ marginTop: 12 }}>{error}</div>}
    </form>
  )
}

export function BackupView({ generated, encrypted, onDone }) {
  const [copied, setCopied] = useState(false)
  const [downloaded, setDownloaded] = useState(false)
  const [confirmed, setConfirmed] = useState(false)

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(generated.nsec)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch { /* noop */ }
  }

  const download = () => {
    try {
      const blob = new Blob([`${generated.nsec}\n`], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `verdant-recovery-key-${generated.pubkey.slice(0, 8)}.txt`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      setDownloaded(true)
      setTimeout(() => setDownloaded(false), 2000)
    } catch { /* noop */ }
  }

  const username = `verdant-${generated.pubkey.slice(0, 8)}`

  return (
    <div className="modal-body">
      <p className="card-summary">
        This is your <strong>recovery key</strong>. Save it to a password
        manager so you can sign in from another device or recover your
        account if you forget your passphrase.
      </p>

      <div className="key-display">
        <code>{generated.nsec}</code>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <button type="button" className="btn btn-ghost" onClick={copy}>
          {copied ? 'Copied!' : 'Copy recovery key'}
        </button>
        <button type="button" className="btn btn-ghost" onClick={download}>
          {downloaded ? 'Downloaded' : 'Download .txt'}
        </button>
      </div>

      {/* Hint form for password managers. autoComplete="new-password" + a
          paired username field is the documented trigger for 1Password,
          iCloud Keychain, and Bitwarden to offer to save credentials. */}
      <form
        className="pm-hint"
        onSubmit={(e) => e.preventDefault()}
        aria-label="Save to password manager"
      >
        <input
          type="text"
          name="username"
          autoComplete="username"
          defaultValue={username}
          readOnly
        />
        <input
          type="password"
          name="new-password"
          autoComplete="new-password"
          defaultValue={generated.nsec}
          readOnly
        />
        <button type="submit" className="btn btn-ghost btn-block">
          Save to password manager
        </button>
      </form>

      <div className="note">
        {encrypted ? (
          <>
            Your account is also protected with the passphrase you chose.
            Keep both safe — there is no password reset.
          </>
        ) : (
          <>
            Your account isn't encrypted. If you clear this browser without
            saving your recovery key, the account is gone for good.
          </>
        )}
      </div>

      <label
        className="checkbox-row"
        style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '12px 0' }}
      >
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)}
        />
        I've saved my recovery key in a safe place.
      </label>

      <button
        type="button"
        className="btn btn-primary btn-block"
        onClick={onDone}
        disabled={!confirmed}
      >
        Continue
      </button>
    </div>
  )
}
